import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useColorTheme } from './ColorThemeContext';
import { User, Theme, Language } from '@redpanda/types';
import {
  hasSystemAccess as utilsHasSystemAccess,
  getInitialTheme,
  initializeLanguage,
  applyTheme,
  updateThemeOnServer,
  updateLanguageOnServer,
  syncLanguage,
  syncUserPreferences,
  ensureCacheOwner,
  performLogoutCleanup,
  createAuthLogout,
} from '@redpanda/utils';
import { checkAuthStatus } from '@redpanda/auth-utils';
import i18n from '../i18n';

const SYSTEM_NAME = 'PM';
const IDENTITY_SERVICE_URL = process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000';
const PRODUCTION_DOMAIN = '.redpanda-tech.com';

const authLogout = createAuthLogout(IDENTITY_SERVICE_URL);

interface UserContextType {
  user: User | null;
  theme: Theme;
  language: Language;
  updateUserTheme: (theme: Theme) => Promise<void>;
  updateUserLanguage: (language: Language) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasSystemAccess: (systemName: string) => boolean;
  refreshUser: () => Promise<void>;
}

export const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [language, setLanguage] = useState<Language>(() => initializeLanguage(i18n.changeLanguage));
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { colorTheme, applyServerColorTheme } = useColorTheme();

  // 應用主題變更
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // 刷新用戶資料
  const refreshUser = useCallback(async () => {
    try {
      const authResult = await checkAuthStatus(IDENTITY_SERVICE_URL, PRODUCTION_DOMAIN, SYSTEM_NAME);

      if (authResult.authenticated && authResult.user) {
        const hasAccess = utilsHasSystemAccess(SYSTEM_NAME, authResult.user);
        if (!hasAccess) {
          console.warn(`User does not have access to ${SYSTEM_NAME} system`);
          setUser(null);
          setIsAuthenticated(false);
          return;
        }

        setUser(authResult.user);
        setIsAuthenticated(true);

        syncUserPreferences(authResult.user, {
          setTheme,
          setLanguage,
          changeLanguage: i18n.changeLanguage,
          setColorTheme: applyServerColorTheme, // 伺服器上的值套回畫面，不再寫回伺服器
          currentTheme: theme,
          currentLanguage: language,
          currentColorTheme: colorTheme,
        });
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  }, [theme, language]);

  // 初始化認證狀態
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);

      try {
        const authResult = await checkAuthStatus(IDENTITY_SERVICE_URL, PRODUCTION_DOMAIN, SYSTEM_NAME);

        if (authResult.authenticated && authResult.user) {
          const hasAccess = utilsHasSystemAccess(SYSTEM_NAME, authResult.user);
          if (!hasAccess) {
            console.warn(`User does not have access to ${SYSTEM_NAME} system`);
            setUser(null);
            setIsAuthenticated(false);
            setIsLoading(false);
            return;
          }

          // 換帳號：快取不是這個帳號的就清掉並重新載入一次（列表、儀表板的 localStorage 快取不分使用者）
          if (ensureCacheOwner(authResult.user)) {
            window.location.reload();
            return;
          }
          setUser(authResult.user);
          setIsAuthenticated(true);

          syncUserPreferences(authResult.user, {
            setTheme,
            setLanguage,
            changeLanguage: i18n.changeLanguage,
            setColorTheme: applyServerColorTheme, // 伺服器上的值套回畫面，不再寫回伺服器
          });
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const updateUserTheme = async (newTheme: Theme) => {
    setTheme(newTheme);
    setUser(prev => prev ? { ...prev, theme: newTheme } : null);
    localStorage.setItem('userTheme', newTheme);

    if (user && isAuthenticated) {
      await updateThemeOnServer(newTheme, IDENTITY_SERVICE_URL);
    }
  };

  const updateUserLanguage = async (newLanguage: Language) => {
    setLanguage(newLanguage);
    setUser(prev => prev ? { ...prev, language: newLanguage } : null);
    syncLanguage(newLanguage, i18n.changeLanguage);

    if (user && isAuthenticated) {
      await updateLanguageOnServer(newLanguage, IDENTITY_SERVICE_URL);
    }
  };

  const hasSystemAccess = (systemName: string): boolean => {
    return utilsHasSystemAccess(systemName, user);
  };

  const logout = useCallback(() => {
    // 清掉 localStorage（含各 store 的持久化資料）、sessionStorage、cookie；之後導向登入頁會重新載入，記憶體中的 store 也一併重置
    // （原本另外呼叫 clearAllStores：它用 webpackIgnore 動態 import 相對路徑，瀏覽器解析不到，從來沒有作用；同 CRM）
    performLogoutCleanup();
    authLogout();
  }, []);

  return (
    <UserContext.Provider value={{
      user,
      theme,
      language,
      updateUserTheme,
      updateUserLanguage,
      logout,
      isAuthenticated,
      isLoading,
      hasSystemAccess,
      refreshUser
    }}>
      {children}
    </UserContext.Provider>
  );
};
